/* eslint-disable sonarjs/cognitive-complexity, react/no-multi-comp */
import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Col, message, Modal, Input, Alert } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { Formik, useFormikContext } from 'formik';
import { Form, Select } from 'formik-antd';
import moment from 'moment';
import { Box, FormInput, CustomInput, Text } from 'atoms';
import { ACL, EditModeBtn } from 'components/common';
import DocumentViewerModal from 'componentsv2/common/modals/DocumentViewerModal';
import ContractEditHistoryTable from 'componentsv2/contract/ContractEditHistoryTable';
import SimilarContracts from 'componentsv2/contract/SimilarContracts';
import { buildChangesDiff, VENDOR_CONTRACT_LOCKED_FIELDS } from './contractEditFlow.helpers';
import {
	createVendorContractV2,
	updateVendorContractV2,
	searchRoutesV2,
	searchVehicleTypesV2,
	getClientContractPerOrderRatesV2,
} from 'store/vendorSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import {
	PageContainer,
	HeaderContainer,
	FormTitle,
	FormSubtitle,
	FormContainer,
	FormRow,
	ButtonContainer,
	SubmitButton,
	CancelButton,
	StyledDatePicker,
	FileUploadButton,
	UploadedFileContainer,
	FileInfo,
	FileIcon,
	FileDetails,
	FileName,
	FileSize,
	RemoveButton,
	ErrorMessage,
} from './VendorContractFormV2.styles';
import {
	BILLING_TYPE_FIXED_VARIABLE,
	BILLING_TYPE_VARIABLE,
	BILLING_TYPE_FIXED_PER_DAY,
	temperatureOptions,
	billingTypeOptions,
	multipleTripsOptions,
	weekOffDaysOptions,
	FormSchema,
	extractFilenameFromS3Key,
} from './VendorContractFormV2.constants';
import PerOrderRatesField, {
	subBillingTypeOptions,
	SUB_BILLING_PER_ORDER,
	makeDefaultRateRows,
} from './PerOrderRatesField';
import {
	handleFileUpload,
	handleRouteFetch,
	handleVehicleTypeFetch,
	handleVehicleFetch,
	formatFileSize,
} from './VendorContractFormV2.helpers';
const API_DATE_FORMAT = 'YYYY-MM-DD';
function PerOrderRatesPrefill({ isEditMode, onPrefillError }) {
	const dispatch = useDispatch();
	const { values, setFieldValue } = useFormikContext();
	const {
		vendor_id,
		project_id,
		vehicle_type_id,
		route_id,
		billing_type,
		billing_subtype,
		contract_date,
		expiry_date,
	} = values;
	const lastFetchKeyRef = useRef('');

	useEffect(() => {
		const isFixedPerDay = billing_type === BILLING_TYPE_FIXED_PER_DAY;
		const isPerOrder =
			(billing_type === BILLING_TYPE_VARIABLE ||
				billing_type === BILLING_TYPE_FIXED_VARIABLE ||
				isFixedPerDay) &&
			billing_subtype === SUB_BILLING_PER_ORDER;
		const shouldFetch = isPerOrder || isFixedPerDay;
		const hasAllFields =
			vendor_id &&
			project_id &&
			vehicle_type_id &&
			route_id &&
			billing_subtype &&
			contract_date &&
			expiry_date;
		if (!shouldFetch || !hasAllFields) return undefined;
		if (isEditMode) return undefined;
		const contractDate = moment(contract_date).format(API_DATE_FORMAT);
		const expiryDate = moment(expiry_date).format(API_DATE_FORMAT);
		const fetchKey = [
			vendor_id,
			project_id,
			vehicle_type_id,
			route_id,
			billing_type,
			billing_subtype,
			contractDate,
			expiryDate,
		].join('|');
		if (lastFetchKeyRef.current === fetchKey) return undefined;
		lastFetchKeyRef.current = fetchKey;

		let cancelled = false;
		onPrefillError(null);
		dispatch(
			getClientContractPerOrderRatesV2({
				project_id,
				vehicle_type_id,
				route_id,
				billing_type,
				billing_subtype,
				contract_date: contractDate,
				expiry_date: expiryDate,
			})
		)
			.then(({ orderRates, fixedRatePerDay, error }) => {
				if (cancelled) return;
				if (error) {
					onPrefillError(error);
					setFieldValue('per_order_rates', []);
					return;
				}
				onPrefillError(null);
				setFieldValue('client_fixed_rate_per_day', fixedRatePerDay);
				if (Array.isArray(orderRates) && orderRates.length) {
					setFieldValue(
						'per_order_rates',
						orderRates.map((r) => ({
							rate_id: r.rate_id,
							// client's rate - read only
							client_rate: r.client_rate ?? null,
							// vendor enters its own rate per line
							rate: '',
						}))
					);
				} else if (billing_subtype === SUB_BILLING_PER_ORDER) {
					setFieldValue('per_order_rates', []);
					message.info('No per-order rates found on the linked client contract');
				}
			})
			.catch(() => {
				if (cancelled) return;
				onPrefillError('Failed to load per-order rates from client contract');
			});

		return () => {
			cancelled = true;
		};
	}, [
		vendor_id,
		project_id,
		vehicle_type_id,
		route_id,
		billing_type,
		billing_subtype,
		contract_date,
		expiry_date,
		isEditMode,
		dispatch,
		setFieldValue,
		onPrefillError,
	]);

	return null;
}

PerOrderRatesPrefill.propTypes = {
	isEditMode: PropTypes.bool,
	onPrefillError: PropTypes.func.isRequired,
};

/**
 * Vendor Contract Form V2 Component
 * Main form component for creating and editing vendor contracts
 */
export default function VendorContractFormV2({
	isEditMode = false,
	isViewMode = false,
	setIsViewMode = () => {},
	formData = {},
	vendorsData = [],
	vehicleData = [],
	projectData = [],
	routeData = [],
}) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [filteredRoutes, setFilteredRoutes] = React.useState(routeData);
	const [filteredVehicleTypes, setFilteredVehicleTypes] = React.useState(vehicleData);
	const [vehicleTypesFullData, setVehicleTypesFullData] = React.useState([]);
	const [vehicleOptions, setVehicleOptions] = React.useState([]);
	const [vehicleOptionsFetching, setVehicleOptionsFetching] = React.useState(false);
	const formValuesRef = React.useRef({});
	const fileInputRef = useRef(null);
	const [uploadedFileName, setUploadedFileName] = React.useState(null);
	const [uploadedFileSize, setUploadedFileSize] = React.useState(null);
	const [fileUploading, setFileUploading] = React.useState(false);
	// eslint-disable-next-line no-unused-vars
	const [uploadedFileUrl, setUploadedFileUrl] = React.useState(null);
	const [documentUploadError, setDocumentUploadError] = React.useState(null);
	const [perOrderPrefillError, setPerOrderPrefillError] = React.useState(null);
	const [documentViewerVisible, setDocumentViewerVisible] = React.useState(false);
	const [viewingDocumentUrl, setViewingDocumentUrl] = React.useState('');
	const [viewingDocumentName, setViewingDocumentName] = React.useState('');
	const [viewingDocumentType, setViewingDocumentType] = React.useState('');
	const DOCUMENT_UPLOAD_ERROR_MESSAGE = 'Please upload the signed contract document first';

	const isEditFlow = isEditMode && Boolean(formData?.id);
	const isLocked = (field) => isEditFlow && VENDOR_CONTRACT_LOCKED_FIELDS.has(field);
	const [editFlowModalOpen, setEditFlowModalOpen] = React.useState(false);
	const [editFlowComments, setEditFlowComments] = React.useState('');
	const [editFlowPending, setEditFlowPending] = React.useState(null);
	const [editFlowSubmitting, setEditFlowSubmitting] = React.useState(false);

	// Handle file removal
	const handleFileRemove = (setFieldValue) => {
		setUploadedFileName(null);
		setUploadedFileSize(null);
		setUploadedFileUrl(null);
		setDocumentUploadError(null);
		setFieldValue('signed_contract_file', null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	// Handle viewing document
	const handleViewDocument = async (documentKey, documentName = 'Signed Contract') => {
		if (!documentKey) {
			message.error('No document key provided');
			return;
		}

		try {
			// Get signed URL for viewing
			const response = await dispatch(
				getSignedUrl({
					url_type: 'download',
					data: {
						entity: 'vendor_contract',
						keys: [documentKey],
					},
				})
			);

			if (response && response.status === 'success' && response.data?.[0]?.url) {
				const documentUrl = response.data[0].url;

				// Determine document type from URL or key
				let docType = 'unknown';
				const lowerKey = documentKey.toLowerCase();
				if (lowerKey.includes('.pdf')) {
					docType = 'pdf';
				} else if (
					lowerKey.includes('.jpg') ||
					lowerKey.includes('.jpeg') ||
					lowerKey.includes('.png')
				) {
					docType = 'image';
				}

				setViewingDocumentUrl(documentUrl);
				setViewingDocumentName(documentName);
				setViewingDocumentType(docType);
				setDocumentViewerVisible(true);
			} else {
				message.error('Failed to load document - Invalid response');
			}
		} catch (error) {
			console.error('Error loading document:', error);
			message.error(`Failed to load document: ${error.message}`);
		}
	};

	// Handle closing document viewer
	const handleCloseDocumentViewer = () => {
		setDocumentViewerVisible(false);
		setViewingDocumentUrl('');
		setViewingDocumentName('');
		setViewingDocumentType('');
	};

	// Initialize file info from formData if in edit mode
	React.useEffect(() => {
		if (formData?.signed_contract_file) {
			const fileName = extractFilenameFromS3Key(formData.signed_contract_file);
			setUploadedFileName(fileName);
			setUploadedFileUrl(formData.signed_contract_file);
			// We don't have the file size from the URL, so we'll just show the name
		}
	}, [formData?.signed_contract_file]);

	// Fetch vehicles on initial load if route_id is already set (edit mode)
	React.useEffect(() => {
		const fetchInitialVehicles = async () => {
			if (formData?.route_id) {
				const existingVehicles = Array.isArray(formData?.vehicles) ? formData.vehicles : [];
				await handleVehicleFetch(
					formData.route_id,
					dispatch,
					setVehicleOptions,
					existingVehicles
				);
			}
		};
		fetchInitialVehicles();
	}, [formData?.route_id, dispatch]);

	// Fetch routes on initial load if project_id and vehicle_type_id are already set (edit mode)
	React.useEffect(() => {
		const fetchInitialRoutes = async () => {
			if (formData?.project_id && formData?.vehicle_type_id) {
				try {
					const routesResponse = await dispatch(
						searchRoutesV2({
							filter_by: {
								project_id: formData.project_id,
								vehicle_type_id: formData.vehicle_type_id,
								is_active: true,
							},
							pagination: { offset: 0, limit: 1000 },
						})
					);

					const transformedRoutes =
						routesResponse?.rows?.map((item) => {
							const routeId = item.id || item.route_id;
							const routeName =
								item.route_name ||
								item.name ||
								item.route_code ||
								`Route ${routeId}` ||
								'-';

							return {
								value: routeId,
								label: routeName,
							};
						}) || [];

					setFilteredRoutes(transformedRoutes);
				} catch (error) {
					console.error('Error fetching initial routes:', error);
				}
			}
		};

		fetchInitialRoutes();
	}, [formData?.project_id, formData?.vehicle_type_id, dispatch]);

	// Fetch vehicle types on initial load if temperature_controlled is already set (edit mode)
	React.useEffect(() => {
		const fetchInitialVehicleTypes = async () => {
			if (
				formData?.temperature_controlled !== undefined &&
				formData?.temperature_controlled !== null
			) {
				try {
					const vehicleTypesResponse = await dispatch(
						searchVehicleTypesV2({
							filter_by: {
								temperature_controlled: formData.temperature_controlled,
							},
							limit: 1000,
						})
					);

					// Store full vehicle type data
					setVehicleTypesFullData(vehicleTypesResponse?.rows || []);

					const transformedVehicleTypes =
						vehicleTypesResponse?.rows?.map((item) => ({
							value: item.id || item.vehicle_type_id || item.vehicle_type,
							label:
								item.vehicle_type_name ||
								item.vehicle_type ||
								item.name ||
								item.label ||
								'-',
						})) || [];

					setFilteredVehicleTypes(transformedVehicleTypes);
				} catch (error) {
					console.error('Error fetching initial vehicle types:', error);
				}
			}
		};

		fetchInitialVehicleTypes();
	}, [formData?.temperature_controlled, vehicleData, dispatch]);

	const initialValues = useMemo(
		() => ({
			...(isEditMode && { id: formData?.id }),
			vendor_id: formData?.vendor_id || undefined,
			temperature_controlled: formData?.temperature_controlled ?? undefined,
			billing_type: formData?.billing_type || undefined,
			billing_subtype: formData?.billing_subtype || undefined,
			per_order_rates: (() => {
				const rates = Array.isArray(formData?.order_rates)
					? formData.order_rates
					: formData?.per_order_rates;
				return Array.isArray(rates) && rates.length ? rates : makeDefaultRateRows();
			})(),
			fixed_hours_per_day: formData?.fixed_hours_per_day ?? '',
			fixed_rate_per_day: formData?.fixed_rate_per_day ?? '',
			client_fixed_rate_per_day: formData?.client_fixed_rate_per_day ?? null,
			threshold_km_per_day: formData?.threshold_km_per_day ?? '',
			loader_cost: formData?.loader_cost ?? '',
			delivery_associate_cost: formData?.delivery_associate_cost ?? '',
			// Backend sends other_expenses_type, map to form field other_expenses
			other_expenses: formData?.other_expenses_type || formData?.other_expenses || '',
			// Backend sends other_expenses_value, map to form field other_other_expenses
			other_other_expenses:
				formData?.other_expenses_value ?? formData?.other_other_expenses ?? '',
			// Pre-populate vehicle_ids from formData.vehicles array
			vehicle_ids: Array.isArray(formData?.vehicles)
				? formData.vehicles.map((v) => v.id ?? v.value)
				: formData?.vehicle_ids || [],
			selected_no_of_vehicles: (() => {
				if (Array.isArray(formData?.vehicles) && formData.vehicles.length > 0)
					return formData.vehicles.length;
				return formData?.selected_no_of_vehicles || '';
			})(),
			multiple_trips: formData?.multiple_trips ?? undefined,
			reporting_time: formData?.reporting_time
				? moment(formData.reporting_time, 'HH:mm:ss')
				: undefined,
			project_id: formData?.project_id || undefined,
			vehicle_type_id: formData?.vehicle_type_id || undefined,
			capacity_tonnes: formData?.capacity_tonnes || 'Enter Capacity',
			contract_date: formData?.contract_date || undefined,
			expiry_date: formData?.expiry_date || undefined,
			threshold_km_per_month: formData?.threshold_km_per_month ?? '',
			vehicle_cost: formData?.vehicle_cost ?? '',
			extra_charges_per_km: formData?.extra_charges_per_km ?? '',
			extra_charges_per_hour: formData?.extra_charges_per_hour ?? '',
			route_id: formData?.route_id || undefined,
			week_off_days: (() => {
				if (!formData?.week_off_days) return [];
				return Array.isArray(formData.week_off_days)
					? formData.week_off_days
					: [formData.week_off_days];
			})(),
			signed_contract_file: formData?.signed_contract_file || null,
		}),
		[formData, isEditMode]
	);

	const handleSubmit = (values, { setErrors, setSubmitting }) => {
		console.log('=== Vendor Contract Form Submit Started ===');
		console.log('Form values:', values);
		console.log('Billing Type:', values.billing_type);
		console.log('Extra Charges Per KM:', values.extra_charges_per_km);
		console.log('Extra Charges Per Hour:', values.extra_charges_per_hour);
		const {
			id,
			vendor_id,
			project_id,
			billing_type,
			billing_subtype,
			per_order_rates,
			temperature_controlled,
			vehicle_type_id,
			signed_contract_file,
			capacity_tonnes,
			contract_date,
			expiry_date,
			fixed_hours_per_day,
			fixed_rate_per_day,
			threshold_km_per_day,
			threshold_km_per_month,
			vehicle_cost,
			loader_cost,
			delivery_associate_cost,
			extra_charges_per_km,
			extra_charges_per_hour,
			other_other_expenses,
			route_id,
			week_off_days,
			reporting_time,
			multiple_trips,
			selected_no_of_vehicles,
		} = values || {};

		// Validate required fields
		if (!vendor_id || !project_id || !vehicle_type_id || !route_id) {
			console.error('Validation failed: Missing required fields', {
				vendor_id,
				project_id,
				vehicle_type_id,
				route_id,
			});
			message.error('Please fill all required fields');
			setSubmitting(false);
			return;
		}

		if (!contract_date || !expiry_date) {
			console.error('Validation failed: Missing dates', { contract_date, expiry_date });
			message.error('Please select contract and expiry dates');
			setSubmitting(false);
			return;
		}

		// Validate vehicle selection
		if (!Array.isArray(values.vehicle_ids) || values.vehicle_ids.length === 0) {
			console.error('Validation failed: No vehicle selected');
			setErrors({ vehicle_ids: 'Please select at least one vehicle' });
			message.error('Please select at least one vehicle');
			setSubmitting(false);
			return;
		}

		// Validate document upload
		if (!signed_contract_file) {
			console.error('Validation failed: Missing signed contract file');
			setDocumentUploadError(DOCUMENT_UPLOAD_ERROR_MESSAGE);
			message.error(DOCUMENT_UPLOAD_ERROR_MESSAGE);
			setSubmitting(false);
			return;
		}

		const isPerOrder = billing_subtype === SUB_BILLING_PER_ORDER;

		if (
			!isPerOrder &&
			(extra_charges_per_km === undefined ||
				extra_charges_per_km === null ||
				extra_charges_per_km === '')
		) {
			console.error('Validation failed: Missing extra charges per km');
			message.error('Extra charges per km is required');
			setSubmitting(false);
			return;
		}

		// Extra charges per hour is only required for Fixed + Variable › Per Kilometer
		if (
			billing_type === BILLING_TYPE_FIXED_VARIABLE &&
			!isPerOrder &&
			(extra_charges_per_hour === undefined ||
				extra_charges_per_hour === null ||
				extra_charges_per_hour === '')
		) {
			console.error('Validation failed: Missing extra charges per hour');
			message.error('Extra charges per hour is required');
			setSubmitting(false);
			return;
		}

		// Build base payload (common fields for all billing types)
		const payload = {
			...(isEditMode && id ? { id } : {}),
			vendor_id,
			project_id,
			billing_type,
			temperature_controlled: Boolean(temperature_controlled),
			route_id,
			vehicle_type_id,
			capacity_tonnes: Number(capacity_tonnes),
			contract_date: moment(contract_date).format(API_DATE_FORMAT),
			expiry_date: moment(expiry_date).format(API_DATE_FORMAT),
			week_off_days: Array.isArray(week_off_days) ? week_off_days.join(',') : week_off_days,
			reporting_time: reporting_time ? moment(reporting_time).format('HH:mm:ss') : null,
			signed_contract: signed_contract_file || null,
			// Required fields that were missing
			multiple_trips,
			selected_no_of_vehicles: Number(selected_no_of_vehicles),
			// Vehicle IDs array (multi-select)
			vehicle_ids: Array.isArray(values.vehicle_ids) ? values.vehicle_ids.map(Number) : [],
			vehicle_cost: Number(vehicle_cost) || 0,
			loader_cost: Number(loader_cost) || 0,
			delivery_associate_cost: Number(delivery_associate_cost) || 0,
		};

		// Sub Billing Type + Per Order rate lines
		if (
			billing_type === BILLING_TYPE_VARIABLE ||
			billing_type === BILLING_TYPE_FIXED_VARIABLE ||
			billing_type === BILLING_TYPE_FIXED_PER_DAY
		) {
			payload.billing_subtype = billing_subtype;
		}
		if (isPerOrder) {
			payload.order_rates = (per_order_rates || []).map((r) => ({
				rate_id: r.rate_id,
				rate: Number(r.rate) || 0,
			}));
		}

		// Add document_info if signed contract is uploaded
		if (signed_contract_file) {
			payload.document_info = {
				signed_contract_file: {
					document_key: signed_contract_file,
				},
			};
		}

		// Add billing type specific fields
		if (billing_type === BILLING_TYPE_FIXED_VARIABLE) {
			// Fixed + Variable specific fields
			payload.fixed_hours_per_day = Number(fixed_hours_per_day);
			payload.threshold_km_per_day = Number(threshold_km_per_day);
			payload.threshold_km_per_month = Number(threshold_km_per_month);
			// other_expenses text field maps to other_expenses_type, other_other_expenses number field maps to other_expenses_value
			if (
				values.other_expenses !== undefined &&
				values.other_expenses !== null &&
				values.other_expenses !== ''
			) {
				payload.other_expenses_type = String(values.other_expenses);
			}
			if (
				other_other_expenses !== undefined &&
				other_other_expenses !== null &&
				other_other_expenses !== ''
			) {
				payload.other_expenses_value = Number(other_other_expenses);
			}
			// Also send as extra_charges_per_trip for compatibility
			if (!isPerOrder) payload.extra_charges_per_trip = Number(extra_charges_per_km) || 0;
		}

		if (billing_type === BILLING_TYPE_FIXED_PER_DAY) {
			payload.fixed_hours_per_day = Number(fixed_hours_per_day);
			payload.threshold_km_per_day = Number(threshold_km_per_day);
			payload.threshold_km_per_month = Number(threshold_km_per_month);
			payload.extra_charges_per_hour = Number(extra_charges_per_hour) || 0;
			payload.fixed_rate_per_day = Number(fixed_rate_per_day) || 0;
			if (
				values.other_expenses !== undefined &&
				values.other_expenses !== null &&
				values.other_expenses !== ''
			) {
				payload.other_expenses_type = String(values.other_expenses);
			}
			if (
				other_other_expenses !== undefined &&
				other_other_expenses !== null &&
				other_other_expenses !== ''
			) {
				payload.other_expenses_value = Number(other_other_expenses);
			}
			if (!isPerOrder) payload.extra_charges_per_trip = Number(extra_charges_per_km) || 0;
		}
		payload.extra_charges_per_km = Number(extra_charges_per_km) || 0;

		// Extra charges per hour applies to Fixed + Variable. Also required by the
		if (billing_type === BILLING_TYPE_FIXED_VARIABLE) {
			payload.extra_charges_per_hour = Number(extra_charges_per_hour) || 0;
		}

		if (billing_type === BILLING_TYPE_VARIABLE) {
			// Variable specific fields
			payload.charges_per_km = Number(extra_charges_per_km);
			if (
				values.other_expenses !== undefined &&
				values.other_expenses !== null &&
				values.other_expenses !== ''
			) {
				payload.other_expenses_type = String(values.other_expenses);
			}
			if (
				other_other_expenses !== undefined &&
				other_other_expenses !== null &&
				other_other_expenses !== ''
			) {
				payload.other_expenses_value = Number(other_other_expenses);
			}
		}

		console.log('Payload to send to API:', payload);
		console.log('Is Edit Mode:', isEditMode);
		console.log('Contract ID:', id);
		if (isEditFlow && id) {
			const changedKeys = Object.keys(
				buildChangesDiff(values, initialValues, VENDOR_CONTRACT_LOCKED_FIELDS)
			);
			const FORM_TO_PAYLOAD_KEY = {
				other_expenses: 'other_expenses_type',
				other_other_expenses: 'other_expenses_value',
				per_order_rates: 'order_rates',
			};
			const changes = {};
			changedKeys.forEach((key) => {
				const payloadKey = FORM_TO_PAYLOAD_KEY[key] || key;
				if (payloadKey in payload) changes[payloadKey] = payload[payloadKey];
			});
			if (
				changedKeys.includes('signed_contract_file') &&
				payload.document_info?.signed_contract_file
			) {
				changes.document_info = {
					signed_contract_file: payload.document_info.signed_contract_file,
				};
				delete changes.signed_contract_file;
			}
			if (Object.keys(changes).length === 0) {
				message.info('No changes detected');
				setSubmitting(false);
				return;
			}
			setEditFlowPending({ id, changes, setErrors });
			setEditFlowModalOpen(true);
			setSubmitting(false);
			return;
		}

		const apiAction = isEditMode && id ? updateVendorContractV2 : createVendorContractV2;

		dispatch(
			apiAction({
				req_data: payload,
				setErrors,
			})
		)
			.then((response) => {
				if (response?.status === 'success' || response?.data) {
					const successMessage =
						response?.message ||
						response?.data?.message ||
						(isEditMode
							? 'Vendor contract updated successfully'
							: 'Vendor contract created successfully');
					console.log('Success! Navigating to vendor contracts list');
					message.success(successMessage);
					setTimeout(() => {
						navigate('/vendor-contracts');
					}, 1000);
				} else {
					console.warn('Unexpected response structure:', response);
					const errorMessage =
						response?.error?.message ||
						response?.message ||
						(isEditMode
							? 'Failed to update vendor contract. Please try again.'
							: 'Failed to create vendor contract. Please try again.');
					message.error(errorMessage);
					setSubmitting(false);
				}
			})
			.catch((error) => {
				console.error('=== API Error Caught ===');
				console.error('Error object:', error);
				console.error('Error response:', error?.response);
				console.error('Error message:', error?.message);
				console.error('Error data:', error?.response?.data);

				// Handle different error response structures
				let errorMessage = isEditMode
					? 'Failed to update vendor contract. Please try again.'
					: 'Failed to create vendor contract. Please try again.';
				const errorData = error?.response?.data;

				// Check for the specific error structure: { or: { code, message: { document_info: "..." } } }
				if (errorData?.or?.message) {
					const orMessage = errorData.or.message;
					// If message is an object (like { document_info: "is a required field." })
					if (typeof orMessage === 'object') {
						// Extract the first error message
						const firstErrorKey = Object.keys(orMessage)[0];
						const firstErrorMessage = orMessage[firstErrorKey];

						if (firstErrorKey === 'document_info') {
							setDocumentUploadError(DOCUMENT_UPLOAD_ERROR_MESSAGE);
							errorMessage = DOCUMENT_UPLOAD_ERROR_MESSAGE;
						} else {
							errorMessage = `${firstErrorKey}: ${firstErrorMessage}`;
						}
					} else if (typeof orMessage === 'string') {
						errorMessage = orMessage;
					}
				} else if (errorData?.error?.message) {
					errorMessage = errorData.error.message;
				} else if (errorData?.message) {
					errorMessage = errorData.message;
				} else if (error?.message) {
					errorMessage = error.message;
				}

				message.error(errorMessage);
				setSubmitting(false);
			})
			.finally(() => {
				console.log('=== Form Submit Process Completed ===');
			});
	};

	const handleEdit = () => {
		setIsViewMode(false);
	};

	const handleCancel = (resetForm) => {
		if (typeof resetForm === 'function') {
			resetForm({ values: initialValues });
		}
		if (formData?.signed_contract_file) {
			setUploadedFileName(extractFilenameFromS3Key(formData.signed_contract_file));
			setUploadedFileUrl(formData.signed_contract_file);
		} else {
			setUploadedFileName(null);
			setUploadedFileUrl(null);
		}
		setUploadedFileSize(null);
		setDocumentUploadError(null);
		setIsViewMode(true);
	};

	const handleEditFlowCancel = () => {
		if (editFlowSubmitting) return;
		setEditFlowModalOpen(false);
		setEditFlowComments('');
		setEditFlowPending(null);
	};

	const handleEditFlowConfirm = async () => {
		const trimmed = (editFlowComments || '').trim();
		if (!trimmed) {
			message.error('Comments are required');
			return;
		}
		if (!editFlowPending) return;

		setEditFlowSubmitting(true);
		try {
			const response = await dispatch(
				updateVendorContractV2({
					req_data: { id: editFlowPending.id },
					edit_flow: true,
					comments: trimmed,
					changes: editFlowPending.changes,
				})
			);
			if (response?.status === 'success' || response?.data) {
				message.success(response?.message || 'Edit submitted for approval');
				setEditFlowModalOpen(false);
				setEditFlowComments('');
				setEditFlowPending(null);
				setTimeout(() => navigate('/vendor-contracts'), 800);
			} else {
				message.error(response?.message || 'Failed to submit edit. Please try again.');
			}
		} catch (error) {
			const rawMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message;
			const errMsg =
				rawMsg && typeof rawMsg === 'object'
					? Object.values(rawMsg).join(', ')
					: rawMsg || 'Failed to submit edit. Please try again.';
			message.error(errMsg);
		} finally {
			setEditFlowSubmitting(false);
		}
	};

	return (
		<PageContainer>
			<HeaderContainer>
				<FormTitle>Vendor Contract</FormTitle>
				<FormSubtitle>
					{isEditMode
						? 'View/Edit vendor contract details'
						: 'Create vendors in the system'}
				</FormSubtitle>
			</HeaderContainer>

			{isEditMode && formData?.has_pending_edit && (
				<Alert
					type="warning"
					showIcon
					message="Pending Edit Approval"
					description="This contract is inactive while an edit is awaiting approval."
					style={{ marginBottom: 16 }}
				/>
			)}

			{isEditMode && isViewMode && !formData?.has_pending_edit && (
				<ACL action="update" module="vendor_contract">
					<EditModeBtn
						handleEdit={handleEdit}
						disabled={
							['rejected', 'pending'].includes(formData?.approval_status) ||
							formData?.is_active === false
						}
					/>
				</ACL>
			)}

			<Formik
				initialValues={initialValues}
				validationSchema={FormSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ values, errors, isSubmitting, submitCount, setFieldValue, resetForm }) => {
					// Log validation state for debugging (only when errors change)
					if (Object.keys(errors).length > 0) {
						console.log('Formik Validation Errors:', errors);
					}

					// Update ref with current values
					formValuesRef.current = values;

					return (
						<FormContainer>
							<Form>
								<SimilarContracts contractType="vendor" excludeId={formData?.id} />
								<PerOrderRatesPrefill
									isEditMode={isEditMode}
									onPrefillError={setPerOrderPrefillError}
								/>
								<FormRow gutter={16}>
									<Col lg={12}>
										<FormInput name="vendor_id" label="Select Vendor *">
											<Select
												name="vendor_id"
												showSearch
												placeholder="Select"
												options={vendorsData}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												disabled={isViewMode || isLocked('vendor_id')}
											/>
										</FormInput>
									</Col>
									<Col lg={12}>
										<FormInput name="project_id" label="Select Project *">
											<Select
												name="project_id"
												showSearch
												placeholder="Select"
												options={projectData}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												disabled={isViewMode || isLocked('project_id')}
												onChange={(value) => {
													setFieldValue('project_id', value);
													// Clear route_id when project changes
													if (formValuesRef.current.route_id) {
														setFieldValue('route_id', undefined);
													}
													// Fetch routes if vehicle type is already selected
													if (
														value &&
														formValuesRef.current.vehicle_type_id
													) {
														handleRouteFetch(
															value,
															formValuesRef.current.vehicle_type_id,
															dispatch,
															setFilteredRoutes
														);
													} else {
														setFilteredRoutes([]);
													}
												}}
											/>
										</FormInput>
									</Col>
								</FormRow>

								<FormRow gutter={16}>
									<Col lg={8}>
										<FormInput
											name="temperature_controlled"
											label="Temperature Controlled *"
										>
											<Select
												name="temperature_controlled"
												showSearch
												placeholder="Select"
												options={temperatureOptions}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												disabled={
													isViewMode || isLocked('temperature_controlled')
												}
												onChange={(value) => {
													setFieldValue('temperature_controlled', value);
													// Clear vehicle_type_id, capacity, and route_id when temperature changes
													if (formValuesRef.current.vehicle_type_id) {
														setFieldValue('vehicle_type_id', undefined);
														setFieldValue('capacity_tonnes', '');
													}
													if (formValuesRef.current.route_id) {
														setFieldValue('route_id', undefined);
													}
													// Fetch vehicle types based on temperature controlled
													handleVehicleTypeFetch(
														value,
														dispatch,
														setFilteredVehicleTypes,
														setVehicleTypesFullData
													);
												}}
											/>
										</FormInput>
									</Col>
									<Col lg={8}>
										<FormInput name="vehicle_type_id" label="Vehicle Type *">
											<Select
												name="vehicle_type_id"
												showSearch
												placeholder={
													values.temperature_controlled !== undefined &&
													values.temperature_controlled !== null
														? 'Select'
														: 'Select Temperature Controlled first'
												}
												options={filteredVehicleTypes}
												disabled={
													isViewMode ||
													isLocked('vehicle_type_id') ||
													values.temperature_controlled === undefined ||
													values.temperature_controlled === null
												}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												onChange={(value) => {
													setFieldValue('vehicle_type_id', value);

													// Find the selected vehicle type and populate capacity
													const selectedVehicleType =
														vehicleTypesFullData.find(
															(vt) =>
																vt.id === value ||
																vt.vehicle_type_id === value ||
																vt.vehicle_type === value
														);

													if (selectedVehicleType) {
														setFieldValue(
															'capacity_tonnes',
															selectedVehicleType.capacity_tonnes ||
																''
														);
													} else {
														setFieldValue('capacity_tonnes', '');
													}

													// Clear route_id when vehicle type changes
													if (formValuesRef.current.route_id) {
														setFieldValue('route_id', undefined);
													}
													// Fetch routes if project is already selected
													if (value && formValuesRef.current.project_id) {
														handleRouteFetch(
															formValuesRef.current.project_id,
															value,
															dispatch,
															setFilteredRoutes
														);
													} else {
														setFilteredRoutes([]);
													}
												}}
											/>
										</FormInput>
									</Col>
									<Col lg={8}>
										<FormInput
											name="capacity_tonnes"
											label="Capacity (in tones)"
										>
											<CustomInput
												name="capacity_tonnes"
												placeholder="Eg: 20"
												disabled
												style={{ backgroundColor: '#f5f5f5' }}
											/>
										</FormInput>
									</Col>
								</FormRow>

								<FormRow gutter={16}>
									<Col lg={8}>
										<FormInput name="route_id" label="Route Name *">
											<Select
												name="route_id"
												showSearch
												placeholder={
													values.project_id && values.vehicle_type_id
														? 'Select'
														: 'Select Project and Vehicle Type first'
												}
												options={filteredRoutes}
												disabled={
													isViewMode ||
													isLocked('route_id') ||
													!values.project_id ||
													!values.vehicle_type_id
												}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												onChange={(value) => {
													setFieldValue('route_id', value);
													// Clear vehicle selections when route changes
													setFieldValue('vehicle_ids', []);
													setFieldValue('selected_no_of_vehicles', '');
													setVehicleOptionsFetching(true);
													handleVehicleFetch(
														value,
														dispatch,
														setVehicleOptions
													).finally(() =>
														setVehicleOptionsFetching(false)
													);
												}}
											/>
										</FormInput>
									</Col>
								</FormRow>

								<FormRow gutter={16}>
									<Col lg={8}>
										<FormInput name="billing_type" label="Billing Type *">
											<Select
												name="billing_type"
												showSearch
												placeholder="Select"
												options={billingTypeOptions}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												disabled={isViewMode || isLocked('billing_type')}
											/>
										</FormInput>
									</Col>
									<Col lg={8}>
										<FormInput name="contract_date" label="Contract Date *">
											<StyledDatePicker
												name="contract_date"
												placeholder="Choose Date"
												format="DD-MM-YYYY"
												disabled={isViewMode}
											/>
										</FormInput>
									</Col>
									<Col lg={8}>
										<FormInput name="expiry_date" label="Expiry Date *">
											<StyledDatePicker
												name="expiry_date"
												placeholder="Choose Date"
												format="DD-MM-YYYY"
												disabled={isViewMode}
											/>
										</FormInput>
									</Col>
								</FormRow>
								{(values.billing_type === BILLING_TYPE_VARIABLE ||
									values.billing_type === BILLING_TYPE_FIXED_VARIABLE ||
									values.billing_type === BILLING_TYPE_FIXED_PER_DAY) && (
									<FormRow gutter={16}>
										<Col lg={8}>
											<FormInput
												name="billing_subtype"
												label="Sub Billing Type *"
											>
												<Select
													name="billing_subtype"
													placeholder="Select"
													options={subBillingTypeOptions}
													disabled={
														isViewMode || isLocked('billing_subtype')
													}
												/>
											</FormInput>
										</Col>
									</FormRow>
								)}

								{(values.billing_type === BILLING_TYPE_FIXED_VARIABLE ||
									values.billing_type === BILLING_TYPE_FIXED_PER_DAY) && (
									<FormRow gutter={16}>
										<Col lg={6}>
											<FormInput
												name="fixed_hours_per_day"
												label="Fixed Hours Per Day *"
											>
												<CustomInput
													name="fixed_hours_per_day"
													placeholder="Eg: 12000"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput
												name="threshold_km_per_day"
												label="Threshold KM Per Day *"
											>
												<CustomInput
													name="threshold_km_per_day"
													placeholder="Eg: 333"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput
												name="threshold_km_per_month"
												label="Threshold KM Per Month *"
											>
												<CustomInput
													name="threshold_km_per_month"
													placeholder="Eg: 4322"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput name="vehicle_cost" label="Vehicle Cost *">
												<CustomInput
													name="vehicle_cost"
													placeholder="Eg: 12,000"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
									</FormRow>
								)}

								{/* Variable cost fields */}
								{values.billing_type === BILLING_TYPE_VARIABLE && (
									<FormRow gutter={16}>
										<Col lg={6}>
											<FormInput name="vehicle_cost" label="Vehicle Cost *">
												<CustomInput
													name="vehicle_cost"
													placeholder="Eg: 12,000"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput name="loader_cost" label="Loader Cost *">
												<CustomInput
													name="loader_cost"
													placeholder="Eg: 517408"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput
												name="delivery_associate_cost"
												label="Delivery Associate Cost *"
											>
												<CustomInput
													name="delivery_associate_cost"
													placeholder="Eg: 517408"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput
												name="extra_charges_per_km"
												label="Extra Charges per km *"
											>
												<CustomInput
													name="extra_charges_per_km"
													placeholder="Eg: 10"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
									</FormRow>
								)}

								{(values.billing_type === BILLING_TYPE_FIXED_VARIABLE ||
									values.billing_type === BILLING_TYPE_FIXED_PER_DAY) && (
									<FormRow gutter={16}>
										<Col lg={6}>
											<FormInput name="loader_cost" label="Loader Cost *">
												<CustomInput
													name="loader_cost"
													placeholder="Eg: 517408"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput
												name="delivery_associate_cost"
												label="Delivery Associate Cost *"
											>
												<CustomInput
													name="delivery_associate_cost"
													placeholder="Eg: 517408"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput
												name="extra_charges_per_km"
												label="Extra Charges per km *"
											>
												<CustomInput
													name="extra_charges_per_km"
													placeholder="Eg: 10"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
										<Col lg={6}>
											<FormInput
												name="extra_charges_per_hour"
												label="Extra Charges Per hour *"
											>
												<CustomInput
													name="extra_charges_per_hour"
													placeholder="Eg: 100"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
									</FormRow>
								)}

								{values.billing_type === BILLING_TYPE_FIXED_PER_DAY && (
									<FormRow gutter={16}>
										<Col lg={6}>
											<FormInput
												name="fixed_rate_per_day"
												label="Fixed Rate Per Day *"
											>
												<CustomInput
													name="fixed_rate_per_day"
													placeholder="Eg: 500"
													disabled={isViewMode}
												/>
											</FormInput>
										</Col>
									</FormRow>
								)}

								{/* Per Order rate grid  */}
								{(values.billing_type === BILLING_TYPE_VARIABLE ||
									values.billing_type === BILLING_TYPE_FIXED_VARIABLE ||
									values.billing_type === BILLING_TYPE_FIXED_PER_DAY) &&
									values.billing_subtype === SUB_BILLING_PER_ORDER &&
									values.vendor_id &&
									values.project_id &&
									values.vehicle_type_id &&
									values.route_id &&
									values.contract_date &&
									values.expiry_date && (
										<FormRow gutter={16}>
											<Col lg={24}>
												{perOrderPrefillError ? (
													<Alert
														type="warning"
														showIcon
														message={perOrderPrefillError}
													/>
												) : (
													<PerOrderRatesField
														isViewMode={isViewMode}
														vendorMode
													/>
												)}
											</Col>
										</FormRow>
									)}

								<FormRow gutter={16}>
									<Col lg={8}>
										<FormInput name="other_expenses" label="Other Expenses">
											<CustomInput
												name="other_expenses"
												placeholder="Eg: Toll Charges"
												disabled={isViewMode}
											/>
										</FormInput>
									</Col>
									<Col lg={8}>
										<FormInput
											name="other_other_expenses"
											label="Enter Other Expenses (₹)"
										>
											<CustomInput
												name="other_other_expenses"
												placeholder="Eg: 500"
												disabled={isViewMode}
											/>
										</FormInput>
									</Col>
								</FormRow>

								<FormRow gutter={16}>
									<Col lg={24}>
										<FormInput name="vehicle_ids" label="Select Vehicles *">
											<Select
												name="vehicle_ids"
												mode="multiple"
												showSearch
												placeholder={
													values.route_id
														? 'Select vehicles'
														: 'Select Route first'
												}
												options={vehicleOptions}
												loading={vehicleOptionsFetching}
												disabled={
													isViewMode ||
													!values.route_id ||
													vehicleOptionsFetching
												}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												onChange={(selectedIds) => {
													setFieldValue('vehicle_ids', selectedIds);
													// Auto-populate count
													setFieldValue(
														'selected_no_of_vehicles',
														selectedIds.length || ''
													);
												}}
											/>
										</FormInput>
									</Col>
								</FormRow>

								<FormRow gutter={16}>
									<Col lg={8}>
										<FormInput
											name="selected_no_of_vehicles"
											label="Selected No. of Vehicles *"
										>
											<CustomInput
												name="selected_no_of_vehicles"
												placeholder="Auto-populated from vehicle selection"
												disabled
												style={{ backgroundColor: '#f5f5f5' }}
											/>
										</FormInput>
									</Col>
									<Col lg={8}>
										<FormInput name="multiple_trips" label="Multiple Trips *">
											<Select
												name="multiple_trips"
												showSearch
												placeholder="Select"
												options={multipleTripsOptions}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												disabled={isViewMode}
											/>
										</FormInput>
									</Col>
									<Col lg={8}>
										<FormInput name="week_off_days" label="Week Off Days">
											<Select
												name="week_off_days"
												mode="multiple"
												showSearch
												placeholder="Select"
												options={weekOffDaysOptions}
												filterOption={(input, option) =>
													(option?.label ?? '')
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												disabled={isViewMode}
											/>
										</FormInput>
									</Col>
								</FormRow>

								<FormRow gutter={16}>
									<Col lg={12}>
										<FormInput name="reporting_time" label="Reporting Time *">
											<StyledDatePicker
												name="reporting_time"
												placeholder="Select Time"
												format="HH:mm:ss"
												showTime
												picker="time"
												disabled={isViewMode}
											/>
										</FormInput>
									</Col>
									<Col lg={12}>
										<Box>
											<Text
												style={{
													fontSize: '14px',
													color: '#1c1c1c',
													marginBottom: '8px',
													display: 'block',
													fontWeight: '500',
												}}
											>
												Upload Signed Contract *
											</Text>
											<input
												type="file"
												ref={fileInputRef}
												style={{ display: 'none' }}
												accept=".pdf,.png,.jpg,.jpeg"
												onChange={(e) => {
													const file = e.target.files?.[0];
													if (file) {
														setDocumentUploadError(null);
														handleFileUpload(
															file,
															dispatch,
															setFileUploading,
															setUploadedFileName,
															setUploadedFileSize,
															setUploadedFileUrl,
															setFieldValue
														);
													}
												}}
												disabled={isViewMode || fileUploading}
											/>
											<FileUploadButton
												onClick={() => fileInputRef.current?.click()}
												disabled={isViewMode || fileUploading}
												loading={fileUploading}
											>
												{fileUploading ? 'Uploading...' : 'Browse File'}
											</FileUploadButton>

											{(documentUploadError ||
												(submitCount > 0 &&
													!values.signed_contract_file)) && (
												<ErrorMessage>
													•{' '}
													{documentUploadError ||
														DOCUMENT_UPLOAD_ERROR_MESSAGE}
												</ErrorMessage>
											)}

											{uploadedFileName && (
												<UploadedFileContainer>
													<FileInfo>
														<FileIcon>📄</FileIcon>
														<FileDetails>
															<FileName>{uploadedFileName}</FileName>
															<FileSize>
																{formatFileSize(uploadedFileSize)}
															</FileSize>
														</FileDetails>
													</FileInfo>
													<Box
														display="flex"
														alignItems="center"
														gap="8px"
													>
														{(uploadedFileUrl ||
															values.signed_contract_file) && (
															<EyeOutlined
																style={{
																	fontSize: 18,
																	color: '#1890ff',
																	cursor: 'pointer',
																}}
																onClick={() =>
																	handleViewDocument(
																		uploadedFileUrl ||
																			values.signed_contract_file,
																		uploadedFileName
																	)
																}
															/>
														)}
														{!isViewMode && (
															<RemoveButton
																onClick={() =>
																	handleFileRemove(setFieldValue)
																}
															>
																✕
															</RemoveButton>
														)}
													</Box>
												</UploadedFileContainer>
											)}
										</Box>
									</Col>
								</FormRow>

								<ButtonContainer>
									{isEditMode && !isViewMode && (
										<CancelButton
											type="button"
											disabled={isSubmitting}
											onClick={() => handleCancel(resetForm)}
										>
											Cancel
										</CancelButton>
									)}
									<SubmitButton
										type="submit"
										htmlType="submit"
										loading={isSubmitting}
										disabled={isSubmitting || isViewMode}
									>
										{isSubmitting ? 'Submitting...' : 'Submit'}
									</SubmitButton>
								</ButtonContainer>
							</Form>
						</FormContainer>
					);
				}}
			</Formik>

			{/* Document Viewer Modal */}
			<DocumentViewerModal
				visible={documentViewerVisible}
				onClose={handleCloseDocumentViewer}
				documentUrl={viewingDocumentUrl}
				documentName={viewingDocumentName}
				documentType={viewingDocumentType}
			/>
			<Modal
				title="Submit Edit for Approval"
				open={editFlowModalOpen}
				onCancel={handleEditFlowCancel}
				onOk={handleEditFlowConfirm}
				okText="Submit"
				cancelText="Cancel"
				confirmLoading={editFlowSubmitting}
				maskClosable={!editFlowSubmitting}
				closable={!editFlowSubmitting}
			>
				<Text style={{ display: 'block', marginBottom: 8 }}>
					Comments <span style={{ color: '#ff4d4f' }}>*</span>
				</Text>
				<Input.TextArea
					rows={4}
					value={editFlowComments}
					onChange={(e) => setEditFlowComments(e.target.value)}
					placeholder="Enter reason for this edit"
					maxLength={500}
				/>
			</Modal>
			{isEditMode && formData?.id && (
				<ContractEditHistoryTable
					contractId={formData?.contract_id || formData?.id}
					contractType="vendor"
				/>
			)}
		</PageContainer>
	);
}

VendorContractFormV2.propTypes = {
	isEditMode: PropTypes.bool,
	isViewMode: PropTypes.bool,
	setIsViewMode: PropTypes.func,
	formData: PropTypes.object,
	vendorsData: PropTypes.array,
	vehicleData: PropTypes.array,
	projectData: PropTypes.array,
	routeData: PropTypes.array,
};
